<script setup lang="ts">
	import { computed, ref } from "vue";
	import { Area, Distance } from "@ptx-autonomous/libs/units";
	import { area as turfArea } from "@turf/area";
	import { distance as turfDistance } from "@turf/distance";
	import { advancedBuffer, formatFeetAndInches } from "@shared/utils";
	import type { PlanPathResult } from "@shared/types";
	import type { Polygon } from "geojson";
	import type { ImplementV2Model } from "@ptx-autonomous/libs/flightpath-service-api-models";
	import WarningIcon from "@/icons/WarningIcon.vue";
	import FloatingTooltip from "@/components/FloatingTooltip.vue";

	const props = defineProps<{
		result: PlanPathResult;
		index: number;
		total: number;
		selected: boolean;
		implement?: ImplementV2Model;
	}>();

	const emit = defineEmits<{
		(e: "select"): void;
	}>();

	const showDetails = ref(false);

	const {
		passOrder,
		pathLengthOptimal,
		workAreaAreaOptimal,
		headlandDepthOptimal,
		efficiencyOptimal,
		pathResult,
	} = props.result;

	const efficiency = computed(() => {
		const swathLength = pathResult?.preMissionMetrics?.swathLength || 0;
		const turnLength = pathResult?.preMissionMetrics?.turnLength || 0;

		if (turnLength === 0) return "100%"; // No turns = perfect efficiency
		if (swathLength === 0) return "0%"; // No work = no efficiency

		const ratio = swathLength / (swathLength + turnLength);
		return `${(ratio * 100).toFixed(0)}%`;
	});

	const workArea = computed(() => {
		if (!pathResult?.preMissionMetrics?.workAreaArea) return "0.00";
		return new Area(pathResult.preMissionMetrics.workAreaArea, "m2")
			.acre()
			.toFixed(2);
	});

	const pathLength = computed(() => {
		return pathResult?.preMissionMetrics?.pathLength?.toFixed(1) || "0.0";
	});

	const optimizations = computed(() => {
		const opts = [];
		if (headlandDepthOptimal) opts.push("Fewest Headlands");
		if (workAreaAreaOptimal) opts.push("Largest Work Area");
		if (pathLengthOptimal) opts.push("Shortest Distance");
		if (efficiencyOptimal) opts.push("Highest Efficiency");
		return opts;
	});

	const hasHighHeadlandDepth = computed(() => {
		return (pathResult?.validDepth || 0) > 2;
	});

	const mitigationImpact = computed(() => {
		if (
			!pathResult?.mitigationData?.shortenedSegments ||
			!props.implement
		) {
			return { area: "0", length: "0" };
		}

		const implementWidth =
			props.implement.Sections?.[0]?.ElementWorkingWidth || 0;

		// Calculate buffered polygons
		const shortenedSegments = pathResult.mitigationData.shortenedSegments
			.map(segment => {
				try {
					const buffered = advancedBuffer(
						{
							type: "Feature",
							properties: {},
							geometry: {
								type: "LineString",
								coordinates: [
									[segment.Start.Long, segment.Start.Lat],
									[segment.End.Long, segment.End.Lat],
								],
							},
						},
						{
							distance: implementWidth / 2,
							units: "meters",
							endCapStyle: "flat",
						},
					);

					if (!buffered?.geometry?.coordinates) return null;
					return buffered.geometry;
				} catch (error) {
					return null;
				}
			})
			.filter((polygon): polygon is Polygon => polygon !== null);

		// Calculate total area
		const totalAreaSqMeters = shortenedSegments.reduce(
			(total, polygon) => total + turfArea(polygon),
			0,
		);

		// Calculate total length
		const totalLengthMeters =
			pathResult.mitigationData.shortenedSegments.reduce(
				(total, segment) => {
					return (
						total +
						turfDistance(
							[segment.Start.Long, segment.Start.Lat],
							[segment.End.Long, segment.End.Lat],
							{ units: "meters" },
						)
					);
				},
				0,
			);

		return {
			area: new Area(totalAreaSqMeters, "m2").acre().toFixed(2),
			length: new Distance(totalLengthMeters, "m").ft().toFixed(0),
		};
	});

	function headlandDepthFeetAndInches(
		pathResult?: PlanPathResult["pathResult"],
	) {
		if (!pathResult || !props.implement) return "0' 0\"";
		const depthMeters =
			(pathResult.validDepth || 0) *
			(props.implement?.Sections![0].ElementWorkingWidth || 0);
		return formatFeetAndInches(depthMeters);
	}
</script>

<template>
	<div
		@click="emit('select')"
		class="group border rounded-lg p-3 cursor-pointer transition-all duration-200"
		:class="[
			selected
				? 'border-green-600 bg-green-50 ring-2 ring-green-600 ring-offset-2'
				: 'border-grey-200 hover:border-green-600 hover:bg-green-50',
		]"
	>
		<!-- Header -->
		<div class="flex items-center justify-between mb-2">
			<div class="text-sm font-medium text-green-950">
				{{
					passOrder === "ConsecutiveOrder"
						? "Consecutive Passes"
						: "Skipped Passes"
				}}
			</div>
			<div class="text-xs text-grey-500">
				{{ index + 1 }} / {{ total }}
			</div>
		</div>

		<!-- High Headland Depth Warning -->
		<div
			v-if="hasHighHeadlandDepth"
			class="mb-2 p-2 bg-orange-50 border border-orange-800 rounded-md"
		>
			<div
				class="flex items-center gap-1 text-orange-800 text-xs font-bold mb-1"
			>
				<WarningIcon class="w-4 h-4" />
				<span>High Headland Depth</span>
			</div>
			<div class="text-xs text-orange-800 font-medium">
				{{ pathResult?.validDepth || 0 }} headland passes required
			</div>
		</div>

		<!-- Path Mitigation Warning -->
		<div
			v-if="pathResult?.mitigationData != null"
			class="mb-2 p-2 bg-yellow-50 border border-yellow-800 rounded-md"
		>
			<div
				class="flex items-center gap-1 text-yellow-800 text-xs font-bold mb-1"
			>
				<WarningIcon class="w-4 h-4" />
				<span>Path Mitigation Applied</span>
			</div>
			<div class="text-xs text-yellow-800 space-y-0.5 font-medium">
				<div>
					{{
						result.pathResult?.mitigationData?.shortenedSegments
							?.length || 0
					}}
					shortened passes
				</div>
				<div
					v-if="mitigationImpact.area !== '0'"
					class="grid grid-cols-2 gap-1 text-xs"
				>
					<div class="font-medium">Impacted Area:</div>
					<div class="text-right">{{ mitigationImpact.area }} AC</div>
					<div>Impacted Length:</div>
					<div class="text-right">
						{{ mitigationImpact.length }} ft
					</div>
				</div>
			</div>
		</div>

		<!-- Key metrics -->
		<div class="space-y-1 text-xs">
			<div class="flex justify-between">
				<div class="text-grey-600 flex items-center">
					<span> Efficiency: </span>
					<FloatingTooltip trigger="click">
						<template #trigger>
							<button
								class="w-3 h-3 ml-1 rounded-full bg-green-600 text-white flex items-center justify-center text-xs hover:bg-green-800 transition-colors"
								type="button"
							>
								?
							</button>
						</template>

						<div class="text-left whitespace-normal max-w-xs">
							Ratio of swath distance to total distance traveled.
							Higher efficiency means less time spent on turns.
						</div>
					</FloatingTooltip>
				</div>
				<span
					:class="{ 'text-green-600 font-medium': efficiencyOptimal }"
				>
					{{ efficiency }}
				</span>
			</div>
			<div class="flex justify-between">
				<span class="text-grey-600">Work Area:</span>
				<span
					:class="{
						'text-green-600 font-medium': workAreaAreaOptimal,
					}"
				>
					{{ workArea }} AC
				</span>
			</div>
			<div class="flex justify-between">
				<span class="text-grey-600">Distance:</span>
				<span
					:class="{ 'text-green-600 font-medium': pathLengthOptimal }"
				>
					{{ pathLength }} mi
				</span>
			</div>
			<div class="flex justify-between">
				<span class="text-grey-600">Headlands:</span>
				<span
					:class="{
						'text-green-600 font-medium': headlandDepthOptimal,
					}"
				>
					{{ pathResult?.validDepth || 0 }}
				</span>
			</div>
			<div class="flex justify-between">
				<span class="text-grey-600">Headland Depth:</span>
				<span
					:class="{
						'text-green-600 font-medium': headlandDepthOptimal,
					}"
				>
					{{ headlandDepthFeetAndInches(pathResult) }}
				</span>
			</div>
		</div>

		<!-- Optimizations -->
		<div
			v-if="optimizations.length > 0"
			class="mt-2"
		>
			<div class="grid grid-cols-2 gap-1 whitespace-nowrap">
				<div
					v-for="opt in optimizations"
					:key="opt"
					class="text-xs text-green-800 px-2 py-0.5 rounded-full font-medium"
					:class="{
						'bg-green-50 group-hover:bg-white': !selected,
						'bg-white': selected,
					}"
				>
					★ {{ opt }}
				</div>
			</div>
		</div>

		<!-- Detailed view toggle -->
		<button
			v-if="selected"
			@click.stop="showDetails = !showDetails"
			class="w-full mt-4 text-xs text-green-600 font-medium"
		>
			{{ showDetails ? "Hide" : "Show" }} Details
		</button>

		<!-- Detailed metrics (expanded view) -->
		<div
			v-if="selected && showDetails"
			class="mt-3 pt-3 border-t border-green-200"
		>
			<div class="grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
				<div class="space-y-1">
					<div class="font-medium text-grey-700 mb-1">Areas</div>
					<div class="flex justify-between">
						<span class="text-grey-600">Total:</span>
						<span
							>{{
								new Area(
									pathResult?.preMissionMetrics?.fieldArea ||
										0,
									"m2",
								)
									.acre()
									.toFixed(2)
							}}
							AC</span
						>
					</div>
					<div class="flex justify-between">
						<span class="text-grey-600">Work:</span>
						<span>{{ workArea }} AC</span>
					</div>
				</div>
				<div class="space-y-1">
					<div class="font-medium text-grey-700 mb-1">Distance</div>
					<div class="flex justify-between">
						<span class="text-grey-600">Total:</span>
						<span>{{ pathLength }} mi</span>
					</div>
					<div class="flex justify-between">
						<span class="text-grey-600">Work:</span>
						<span
							>{{
								pathResult?.preMissionMetrics?.swathLength?.toFixed(
									1,
								) || "0.0"
							}}
							mi</span
						>
					</div>
					<div class="flex justify-between">
						<span class="text-grey-600">Turn:</span>
						<span
							>{{
								pathResult?.preMissionMetrics?.turnLength?.toFixed(
									1,
								) || "0.0"
							}}
							mi</span
						>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
